import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import { Header } from './components/Header';
import { Sudoku } from './components/games/Sudoku';
import Login from './components/Login';
import Signup from './components/Signup';
// import Dashboard from './components/Dashboard';
import FifteenPuzzle from './components/games/FifteenPuzzle';
import Game2048 from './components/games/Game2048';
// import Hangman from './components/games/Hangman';
import About from './components/About';
import PrivacyPolicy from './components/PrivacyPolicy';
import Footer from './components/Footer';
import { Toaster } from 'sonner';
import { usePageAnalytics } from './hooks/useAnalytics';
import { debug } from './lib/utils';
import { getDateSeed } from './lib/dateSeed';
import { ProfilePage } from './components/Profile';
import { AnalyticsConsentControls } from './components/AnalyticsConsent';

// Analytics wrapper component
const AnalyticsWrapper = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

const AnalyticsObserver = () => {
  usePageAnalytics();
  return null;
};

// Protected route component that checks if user is authenticated
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

function App() {
  const today = new Date();

  // Log today and the next 13 days with their seeds
  const formatDate = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  for (let i = 0; i < 14; i++) {
    const currentDate = new Date(today);
    currentDate.setDate(today.getDate() + i);
    const difficulty = currentDate.getDay() % 4 + 1;
    const seed = getDateSeed(currentDate, difficulty);
    const seed1 = getDateSeed(currentDate, 1);
    const previewDate = currentDate.toISOString().split('T')[0];
    debug(`Day ${i}: ${formatDate(currentDate)} (${difficulty})`);
    debug(`Sudoku de hoy: www.doganju.com/sudoku/${seed}`);
    debug(`http://localhost:5173/sudoku/preview/${previewDate}/${difficulty}`);
    debug(`                    Juego del 15 de hoy: www.doganju.com/15puzzle/${seed1}`);
    debug(`                    http://localhost:5173/15puzzle/preview/${previewDate}/1`);
    debug(`2048 de hoy: www.doganju.com/2048/${seed}`);
    debug(`http://localhost:5173/2048/preview/${previewDate}/${difficulty}`);
  }

  return (
    <Router>
      <AuthProvider>
        <AnalyticsObserver />
        <div className="flex flex-col min-h-screen">
          <Header />
          <main className="flex-grow">
            <Routes>
              <Route
                path="/"
                element={
                  <AnalyticsWrapper>
                    <Sudoku />
                  </AnalyticsWrapper>
                }
              />
              <Route
                path="/sudoku"
                element={
                  <AnalyticsWrapper>
                    <Sudoku />
                  </AnalyticsWrapper>
                }
              />
              <Route
                path="/sudoku/:seed"
                element={
                  <AnalyticsWrapper>
                    <Sudoku />
                  </AnalyticsWrapper>
                }
              />
              <Route
                path="/sudoku/preview/:previewDate/:previewDifficulty"
                element={
                  <AnalyticsWrapper>
                    <Sudoku />
                  </AnalyticsWrapper>
                }
              />
              <Route
                path="/15puzzle"
                element={
                  <AnalyticsWrapper>
                    <FifteenPuzzle />
                  </AnalyticsWrapper>
                }
              />
              <Route
                path="/15puzzle/:seed"
                element={
                  <AnalyticsWrapper>
                    <FifteenPuzzle />
                  </AnalyticsWrapper>
                }
              />
              <Route
                path="/15puzzle/preview/:previewDate/:previewDifficulty"
                element={
                  <AnalyticsWrapper>
                    <FifteenPuzzle />
                  </AnalyticsWrapper>
                }
              />
              <Route
                path="/2048"
                element={
                  <AnalyticsWrapper>
                    <Game2048 />
                  </AnalyticsWrapper>
                }
              />
              <Route
                path="/2048/:seed"
                element={
                  <AnalyticsWrapper>
                    <Game2048 />
                  </AnalyticsWrapper>
                }
              />
              <Route
                path="/2048/preview/:previewDate/:previewDifficulty"
                element={
                  <AnalyticsWrapper>
                    <Game2048 />
                  </AnalyticsWrapper>
                }
              />
              <Route
                path="/about"
                element={
                  <AnalyticsWrapper>
                    <About />
                  </AnalyticsWrapper>
                }
              />
              <Route
                path="/privacy-policy"
                element={
                  <AnalyticsWrapper>
                    <PrivacyPolicy />
                  </AnalyticsWrapper>
                }
              />
              <Route
                path="/login"
                element={
                  <AnalyticsWrapper>
                    <Login />
                  </AnalyticsWrapper>
                }
              />
              <Route
                path="/signup"
                element={
                  <AnalyticsWrapper>
                    <Signup />
                  </AnalyticsWrapper>
                }
              />
              {/* <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <AnalyticsWrapper>
                      <Dashboard />
                    </AnalyticsWrapper>
                  </ProtectedRoute>
                }
              /> */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <AnalyticsWrapper>
                      <ProfilePage />
                    </AnalyticsWrapper>
                  </ProtectedRoute>
                }
              />
              {/* Default route - will catch any unmatched paths and redirect to /sudoku */}
              <Route
                path="*"
                element={<Navigate to="/sudoku" replace />}
              />
            </Routes>
          </main>
          <Footer />
          <AnalyticsConsentControls />
          <Toaster position="top-center" toastOptions={{ classNames: { toast: '!rounded-xl !border-slate-400 !bg-white !text-midnight !shadow-lg dark:!border-slate-500 dark:!bg-slate-800 dark:!text-slate-100', description: '!text-slate-600 dark:!text-slate-300' } }} />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
